import React, { useState, useEffect, useRef, useCallback } from "react";
import { CELL_SIZE, GRID_PADDING } from "../constants";
import { getSectionAvailability } from "../utils";

const UserFloorPlan = ({
  office,
  sections,
  bookings,
  selectedDate,
  requirement,
  selectedSectionId,
  onSectionClick,
  availableSeats,
  blockedSeatIds,
  reservedSeatIds,
  permanentSeatIds,
  recurringSeatIds,
}) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  const gridWidth = office.gridColumns * CELL_SIZE + GRID_PADDING * 2;
  const gridHeight = office.gridRows * CELL_SIZE + GRID_PADDING * 2;

  const computeScale = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    if (containerWidth < gridWidth) {
      setScale(0.7);
    } else {
      setScale(1);
    }
  }, [gridWidth]);

  useEffect(() => {
    computeScale();
    window.addEventListener("resize", computeScale);
    return () => window.removeEventListener("resize", computeScale);
  }, [computeScale]);

  return (
    <div className="bg-white rounded-[24px] border border-[#d8d5cc] overflow-hidden shadow-xl">
      {/* Grey Based Header Section with Legend Back Inside */}
      <div className="flex flex-wrap items-center justify-between px-8 py-6 border-b border-[#d8d5cc] bg-[#e8e5dc]">
        <div className="flex flex-col gap-1">
          <p className="text-[18px] font-black text-[#1a1a1a]">
            Office Map & Seat Availability
          </p>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)] animate-pulse" />
            <span className="text-[12px] font-bold text-[#7c7a72] ">
              Live Availability View
            </span>
          </div>
        </div>

        {/* Legend - Back in the header but with grey theme compatibility */}
        <div className="flex mt-2 lg:mt-0 items-center gap-3 sm:gap-5 bg-white/50 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-slate-200 shadow-sm">
          <span className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-[11px] font-black tracking-normal sm:tracking-widest text-emerald-700">
            <span className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]" />
            Available
          </span>

          <span className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-[11px] font-black tracking-normal sm:tracking-widest text-amber-700">
            <span className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]" />
            Limited
          </span>

          <span className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-[11px] font-black tracking-normal sm:tracking-widest text-rose-700">
            <span className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]" />
            Full
          </span>

          <span className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-[11px] font-black tracking-normal sm:tracking-widest text-slate-500">
            <span className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-slate-400 shadow-[0_0_8px_rgba(148,163,184,0.35)]" />
            Unavailable
          </span>
        </div>
      </div>

      {/* Grid Container — scales down on screens up to lg */}
      <div
        ref={containerRef}
        className="overflow-x-auto max-h-[750px] pt-4 px-4 pb-8 sm:pt-6 sm:px-6 sm:pb-12 lg:pt-10 lg:px-10 lg:pb-16 bg-white thin-horizontal-scroll"
      >
        <div
          style={{
            width: gridWidth * scale,
            height: gridHeight * scale,
          }}
        >
          <div
            className="relative mx-auto rounded-3xl border border-slate-200 bg-white shadow-inner"
            style={{
              height: gridHeight,
              width: gridWidth,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              backgroundImage:
                "linear-gradient(to right, rgba(148,163,184,0.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.25) 1px, transparent 1px)",
              backgroundSize: `${CELL_SIZE}px ${CELL_SIZE}px`,
              backgroundPosition: `${GRID_PADDING}px ${GRID_PADDING}px`,
            }}
          >
            {sections.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400 font-bold uppercase tracking-widest">
                No sections configured
              </div>
            )}

            {sections.map((section) => {
              const hasDate = !!selectedDate;
              const { total, available } = hasDate
                ? getSectionAvailability(
                    section,
                    bookings,
                    selectedDate,
                    requirement,
                    availableSeats,
                    blockedSeatIds,
                    reservedSeatIds,
                    permanentSeatIds,
                    recurringSeatIds,
                  )
                : { total: section.seats?.length || 0, available: -1 };
              const isSelected = selectedSectionId === section.id;

              let borderColor = "border-slate-200";
              let bgColor = "bg-white";
              let dotColor = "bg-slate-300";
              let selectedColor = "#0f172a"; // default slate-900

              if (!hasDate) {
                // Neutral state — no date selected yet
                borderColor = "border-slate-200";
                bgColor = "bg-slate-50";
                dotColor = "bg-slate-300";
              } else if (total === 0) {
                borderColor = "border-slate-300";
                bgColor = "bg-slate-100/80";
              } else if (available === 0) {
                // Filled
                borderColor = "border-rose-300";
                bgColor = "bg-rose-50";
                dotColor = "bg-rose-500";
                selectedColor = "#f43f5e"; // rose-500
              } else if (available < total / 2) {
                // Limited
                borderColor = "border-amber-300";
                bgColor = "bg-amber-50";
                dotColor = "bg-amber-500";
                selectedColor = "#f59e0b"; // amber-500
              } else {
                // Available
                borderColor = "border-emerald-300";
                bgColor = "bg-emerald-50";
                dotColor = "bg-emerald-500";
                selectedColor = "#10b981"; // emerald-500
              }

              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => onSectionClick(section)}
                  className={`absolute rounded-2xl border p-2 text-center transition-all duration-300 ${borderColor} ${bgColor} ${
                    !hasDate || total === 0
                      ? "cursor-pointer hover:shadow-md"
                      : "cursor-pointer hover:shadow-xl hover:scale-[1.02]"
                  }`}
                  style={{
                    left: (section.x - 1) * CELL_SIZE + GRID_PADDING,
                    top: (section.y - 1) * CELL_SIZE + GRID_PADDING,
                    width: section.width * CELL_SIZE,
                    height: section.height * CELL_SIZE,
                    ...(isSelected && {
                      outline: `2px solid ${selectedColor}`,
                      outlineOffset: "-2px",
                      boxShadow: "0 20px 40px -10px rgba(0,0,0,0.2)",
                    }),
                  }}
                >
                  <div className="flex h-full flex-col items-center justify-center gap-1.5">
                    <div
                      className={`h-1.5 w-1.5 rounded-full ${dotColor} shadow-sm`}
                    />
                    <p className="text-[13px] font-black text-slate-900 leading-tight uppercase tracking-tight">
                      {section.name}
                    </p>
                    {hasDate && (total > 0 ? (
                      <div className="flex flex-col items-center">
                        <p
                          className={`text-[11px] font-black uppercase tracking-widest ${
                            available === 0
                              ? "text-rose-600"
                              : available < total / 2
                                ? "text-amber-600"
                                : "text-emerald-600"
                          }`}
                        >
                          {available}/{total} FREE
                        </p>
                      </div>
                    ) : (
                      <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">
                        Unavailable
                      </p>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserFloorPlan;
